import { useState } from "react";
import { Button } from "./ui/button";
import { Loader2, CreditCard, Building2, Banknote, Phone } from "lucide-react";

// Public pay page panel used when the credit card fee is turned on.
// Every way to pay and its exact total is on screen before the customer pays.
const money = (n) => Number(n || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function CardFeePayPanel({ remaining, pct, bizPhone, bizPhoneDigits, paying, error, onPay }) {
  const [mode, setMode] = useState("bank");
  const fee = Math.round(Number((remaining * pct).toFixed(6))) / 100;
  const cardTotal = Math.round((remaining + fee) * 100) / 100;
  const pctLabel = Number(pct).toString();
  const options = [
    { key: "bank", icon: Building2, title: "Bank transfer (ACH)", note: "No fee", amount: remaining },
    { key: "card", icon: CreditCard, title: "Credit card", note: `Includes ${pctLabel}% card fee ($${money(fee)})`, amount: cardTotal },
    { key: "cash", icon: Banknote, title: "Cash, check or Zelle", note: "No fee", amount: remaining },
  ];
  return (
    <div className="space-y-3" data-testid="card-fee-pay-panel">
      <p className="text-sm font-bold text-slate-800">Choose how to pay</p>
      <div className="space-y-2">
        {options.map((o) => {
          const Icon = o.icon;
          const on = mode === o.key;
          return (
            <button
              key={o.key}
              type="button"
              onClick={() => setMode(o.key)}
              className={`w-full flex items-center justify-between gap-3 p-3 rounded-xl border-2 text-left transition-all ${on ? "border-[#0F4C81] bg-[#0F4C81]/5" : "border-slate-200 hover:border-slate-300 bg-white"}`}
              data-testid={`pay-option-${o.key}`}
            >
              <span className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${on ? "text-[#0F4C81]" : "text-slate-400"}`} />
                <span>
                  <span className="block text-sm font-semibold text-slate-900">{o.title}</span>
                  <span className={`block text-xs ${o.key === "card" ? "text-slate-500" : "text-emerald-700 font-semibold"}`}>{o.note}</span>
                </span>
              </span>
              <span className="text-base font-bold text-slate-900 tabular-nums">${money(o.amount)}</span>
            </button>
          );
        })}
      </div>
      <p className="text-xs text-slate-600 bg-slate-100 rounded-lg px-3 py-2 leading-relaxed" data-testid="card-fee-disclosure">
        Credit cards carry a {pctLabel}% processing fee. That is what the card company charges us, and we pass it through at cost. We do not make anything on it. Bank transfer, debit cards, cash, check and Zelle have no fee. If you pay with a debit card, the fee is refunded to you automatically.
      </p>
      {mode === "cash" ? (
        <div className="rounded-xl border-2 border-emerald-200 bg-emerald-50 p-4 space-y-3" data-testid="cash-pay-panel">
          <ul className="text-sm text-slate-700 space-y-1.5 list-disc list-inside">
            <li><span className="font-semibold">Zelle</span> to {bizPhone}</li>
            <li><span className="font-semibold">Cash App</span> to {bizPhone}</li>
            <li><span className="font-semibold">Cash or check</span> handed to the crew on site</li>
          </ul>
          <a
            href={`tel:+1${bizPhoneDigits}`}
            className="flex items-center justify-center gap-2 w-full h-12 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold transition-colors"
            data-testid="cash-pay-call-btn"
          >
            <Phone className="w-4 h-4" />
            Call / text {bizPhone}
          </a>
        </div>
      ) : (
        <Button
          onClick={() => onPay(mode)}
          disabled={paying}
          className="w-full h-14 bg-[#0F4C81] hover:bg-[#0D4270] text-base font-bold rounded-xl"
          data-testid="pay-now-btn"
        >
          {paying ? (
            <><Loader2 className="w-5 h-5 animate-spin mr-2" />Processing...</>
          ) : mode === "card" ? (
            <><CreditCard className="w-5 h-5 mr-2" />Pay ${money(cardTotal)} by credit card</>
          ) : (
            <><Building2 className="w-5 h-5 mr-2" />Pay ${money(remaining)} by bank transfer</>
          )}
        </Button>
      )}
      {mode === "bank" && <p className="text-xs text-slate-500 text-center">Secure bank payment by Stripe. It takes up to 4 business days to clear.</p>}
      {mode === "card" && <p className="text-xs text-slate-500 text-center">Secure card payment by Stripe. Apple Pay and Google Pay work here too.</p>}
      {error && <p className="text-sm text-red-500 text-center" data-testid="payment-error">{error}</p>}
    </div>
  );
}
